/**
 * Title, freshness, and the controls that act on the dashboard itself.
 *
 * The split from FilterBar is deliberate: refresh, auto-refresh and the theme change
 * *how* the page behaves, while the filter row changes *what* it shows. Mixing the two
 * makes a reader hunt for the control that narrows the data.
 */

import { LogOut, Moon, RefreshCw, Sun } from 'lucide-react'

import { cn } from '../lib/cn'
import { formatRelative } from '../lib/format'
import type { ResolvedTheme } from '../hooks/useTheme'
import { BrandMark } from './ui/BrandMark'
import { IntervalControl } from './ui/IntervalControl'

interface HeaderProps {
  lastPollAt: number | null
  pollIntervalSeconds: number
  autoRefresh: boolean
  onAutoRefreshChange: (value: boolean) => void
  onRefresh: () => void
  isRefreshing: boolean
  theme: ResolvedTheme
  onToggleTheme: () => void
  /** Shown when the backend is reachable but its own picture has gone stale. */
  staleWarning?: string | null
  /** Change the global default poll interval. Omitted on the detail page. */
  onSetGlobalInterval?: (seconds: number) => void
  globalIntervalPending?: boolean
  /** Sign out. Present only when auth is enabled. */
  onLogout?: () => void
  logoutPending?: boolean
}

export function Header({
  lastPollAt,
  pollIntervalSeconds,
  autoRefresh,
  onAutoRefreshChange,
  onRefresh,
  isRefreshing,
  theme,
  onToggleTheme,
  staleWarning,
  onSetGlobalInterval,
  globalIntervalPending,
  onLogout,
  logoutPending,
}: HeaderProps) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <BrandMark size={26} className="shrink-0" />
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-ink">
            WhiteHawk Health
          </h1>
          <p className="text-xs text-ink-secondary">
            {staleWarning ? (
              <span className="text-warning">{staleWarning}</span>
            ) : (
              <>
                Polled every {pollIntervalSeconds}s · last poll{' '}
                {formatRelative(lastPollAt)}
              </>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {onSetGlobalInterval ? (
          <IntervalControl
            seconds={pollIntervalSeconds}
            onChange={(seconds) => seconds && onSetGlobalInterval(seconds)}
            pending={globalIntervalPending}
            label="Poll every"
            className="mr-1"
          />
        ) : null}

        <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(event) => onAutoRefreshChange(event.target.checked)}
            className="h-3.5 w-3.5 accent-[var(--color-accent)]"
          />
          Auto-refresh
        </label>

        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium',
            'text-ink transition-colors hover:bg-surface-sunken disabled:opacity-60',
          )}
        >
          <RefreshCw
            size={14}
            aria-hidden
            className={isRefreshing ? 'animate-spin' : undefined}
          />
          {isRefreshing ? 'Probing…' : 'Probe now'}
        </button>

        <button
          type="button"
          onClick={onToggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          className="inline-flex items-center rounded-md border p-1.5 text-ink transition-colors hover:bg-surface-sunken"
        >
          {theme === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
        </button>

        {onLogout ? (
          <button
            type="button"
            onClick={onLogout}
            disabled={logoutPending}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-sunken disabled:opacity-60"
          >
            <LogOut size={14} aria-hidden />
            {logoutPending ? 'Signing out…' : 'Sign out'}
          </button>
        ) : null}
      </div>
    </header>
  )
}
